import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, Loader2, Eye, EyeOff, ShieldAlert, LogOut } from 'lucide-react';

const CambiaPasswordPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const forced = !!user?.must_change_password;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('La nuova password deve essere di almeno 6 caratteri');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Le due password non coincidono');
      return;
    }
    if (newPassword === currentPassword) {
      toast.error('La nuova password deve essere diversa da quella attuale');
      return;
    }
    setSubmitting(true);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      await refreshUser();
      toast.success('Password aggiornata con successo');
      navigate('/', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-6 bg-background">
      <Card className="w-full max-w-md" data-testid="change-password-card">
        <CardHeader className="space-y-3 text-center">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
            <Lock className="w-6 h-6 text-primary" />
          </div>
          <CardTitle className="text-2xl font-heading">
            {forced ? 'Imposta una nuova password' : 'Cambia password'}
          </CardTitle>
          {forced && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-sm text-left">
              <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5" />
              <p>
                Per motivi di sicurezza, al primo accesso devi sostituire la password temporanea
                con una nuova personale.
              </p>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Password attuale</Label>
              <div className="relative">
                <Input
                  data-testid="current-password-input"
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full"
                  onClick={() => setShowCurrent((v) => !v)}
                >
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Nuova password</Label>
              <div className="relative">
                <Input
                  data-testid="new-password-input"
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={6}
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full"
                  onClick={() => setShowNew((v) => !v)}
                >
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Almeno 6 caratteri</p>
            </div>

            <div className="space-y-2">
              <Label>Conferma nuova password</Label>
              <Input
                data-testid="confirm-password-input"
                type={showNew ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
              data-testid="submit-change-password"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
              Aggiorna password
            </Button>

            {forced && (
              <Button
                type="button"
                variant="ghost"
                className="w-full text-muted-foreground"
                onClick={logout}
                data-testid="logout-from-change-password"
              >
                <LogOut className="w-4 h-4 mr-2" /> Esci e ritorna al login
              </Button>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CambiaPasswordPage;
